import { useState } from "react";
import { toast } from "sonner";
import { X, Send, Loader2, FileText, ExternalLink, AlertTriangle, Briefcase } from "lucide-react";
import { requestRfq, previewRfqPdfUrl } from "@/lib/api";

export default function RequestRfqDialog({ quote, emailStatus, onClose, onSent }) {
  const isFR = quote.language === "fr";
  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [senderName, setSenderName] = useState("2RA-press");
  const [senderEmail, setSenderEmail] = useState("");
  const [deadline, setDeadline] = useState("");
  const [language, setLanguage] = useState(isFR ? "fr" : "en");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const configured = emailStatus?.configured;

  const send = async () => {
    if (!recipientEmail) {
      toast.error("Please enter the printer's email");
      return;
    }
    setSending(true);
    try {
      const res = await requestRfq(quote.id, {
        recipient_name: recipientName,
        recipient_email: recipientEmail,
        sender_name: senderName,
        sender_email: senderEmail || null,
        deadline,
        subject: subject || null,
        message: message || null,
        language,
      });
      toast.success(`RFQ sent to ${res.to}`);
      onSent?.(res);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Send failed";
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  const previewParams = {
    recipient_name: recipientName,
    sender_name: senderName,
    sender_email: senderEmail,
    deadline,
    language,
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="rfq-dialog">
      <div className="bg-white border border-[#050505] w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="border-b border-[#050505] px-6 py-5 flex items-center justify-between sticky top-0 bg-white">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 bg-[#FF3333] text-white flex items-center justify-center mt-0.5 shrink-0">
              <Briefcase className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <div>
              <div className="text-label mb-1">Request Printer Quote</div>
              <h2 className="font-display font-bold text-2xl tracking-tight uppercase">
                Send to a printer
              </h2>
              <div className="text-xs text-[#525252] mt-1.5">
                Specs only — no prices, no markup. The printer replies with their own quote.
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[#f5f5f5]" data-testid="rfq-close" aria-label="Close">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {!configured && (
            <div className="border border-[#FF3333] bg-[#FFF5F5] p-4 text-sm">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-4 h-4 text-[#FF3333] mt-0.5 shrink-0" strokeWidth={1.75} />
                <div>
                  <div className="font-bold text-[#FF3333] mb-1">Email is not configured.</div>
                  <div className="text-[#525252] text-xs leading-relaxed">
                    Add your <code className="font-mono">RESEND_API_KEY</code> to{" "}
                    <code className="font-mono">backend/.env</code> and restart the backend.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Recipient */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="swiss-label">Printer name</span>
              <input
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                placeholder="GreatWriters Press"
                className="swiss-input"
                data-testid="rfq-recipient-name"
              />
            </label>
            <label className="block">
              <span className="swiss-label">Printer email *</span>
              <input
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="sales@greatwriters.co.za"
                className="swiss-input"
                data-testid="rfq-recipient-email"
              />
            </label>
          </div>

          {/* Sender (you) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="swiss-label">Your name / company</span>
              <input
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
                className="swiss-input"
                data-testid="rfq-sender-name"
              />
            </label>
            <label className="block">
              <span className="swiss-label">Your reply-to email</span>
              <input
                type="email"
                value={senderEmail}
                onChange={(e) => setSenderEmail(e.target.value)}
                placeholder="you@2ra-press.com"
                className="swiss-input"
                data-testid="rfq-sender-email"
              />
            </label>
          </div>

          {/* Deadline + Language */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="swiss-label">Requested deadline (optional)</span>
              <input
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                placeholder="Jul 15, 2026"
                className="swiss-input"
                data-testid="rfq-deadline"
              />
            </label>
            <label className="block">
              <span className="swiss-label">RFQ language</span>
              <div className="flex">
                {[
                  { code: "en", label: "English" },
                  { code: "fr", label: "Français" },
                ].map((opt) => (
                  <button
                    key={opt.code}
                    type="button"
                    onClick={() => setLanguage(opt.code)}
                    data-testid={`rfq-lang-${opt.code}`}
                    className={`flex-1 h-[42px] text-[11px] uppercase tracking-[0.18em] font-bold border border-[#050505] transition-colors ${
                      language === opt.code ? "bg-[#050505] text-white" : "bg-white text-[#050505] hover:bg-[#f5f5f5]"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </label>
          </div>

          {/* Subject + Message overrides */}
          <label className="block">
            <span className="swiss-label">Subject (auto-generated if blank)</span>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={
                language === "fr"
                  ? `Demande de devis ${quote.quote_number}`
                  : `Request for quotation ${quote.quote_number}`
              }
              className="swiss-input"
              data-testid="rfq-subject"
            />
          </label>
          <label className="block">
            <span className="swiss-label">Custom message (optional)</span>
            <textarea
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={
                language === "fr"
                  ? "Laisser vide pour utiliser le message par défaut."
                  : "Leave blank to use the default message."
              }
              className="swiss-input resize-y"
              data-testid="rfq-message"
            />
          </label>

          {/* Attachment preview */}
          <div className="border border-[#050505] bg-[#f5f5f5] p-4 flex items-start gap-3">
            <FileText className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} />
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold mb-1">
                Attachment: RFQ-{quote.quote_number}.pdf
              </div>
              <div className="text-xs text-[#525252] leading-relaxed">
                Specs-only request — no prices, no markup. The printer sees what to print, not what you charge.
              </div>
            </div>
            <a
              href={previewRfqPdfUrl(quote.id, previewParams)}
              target="_blank"
              rel="noreferrer"
              className="text-xs uppercase tracking-[0.18em] font-bold ghost-link inline-flex items-center gap-1 shrink-0"
              data-testid="rfq-preview-link"
            >
              Preview <ExternalLink className="w-3 h-3" strokeWidth={1.75} />
            </a>
          </div>
        </div>

        <div className="border-t border-[#050505] px-6 py-4 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="btn-secondary" data-testid="rfq-cancel">
            Cancel
          </button>
          <button
            onClick={send}
            disabled={sending || !configured || !recipientEmail}
            className="btn-primary"
            data-testid="rfq-send"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={1.5} />}
            {sending ? "Sending…" : "Send RFQ"}
          </button>
        </div>
      </div>
    </div>
  );
}
